import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { supabase } from '../lib/supabase'
import { resizeImage } from '../lib/resizeImage'
import { decodeEntities } from '../lib/decodeEntities'
import SEO from '../components/SEO'

const CATEGORIES = ['General', 'Social', 'Writing', 'Fanfiction', 'Anime', 'Books', 'TV & Film', 'Music', 'Gaming', 'Art', 'LGBTQ+', 'Other']

const GRADIENTS = [
  'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
  'linear-gradient(135deg, #0D9488 0%, #1D4ED8 100%)',
  'linear-gradient(135deg, #EA580C 0%, #EAB308 100%)',
  'linear-gradient(135deg, #BE123C 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #16A34A 0%, #0D9488 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #EC4899 100%)',
  'linear-gradient(135deg, #374151 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #B45309 0%, #DC2626 100%)',
]

const EMOJIS = ['✨', '📚', '🎭', '🌸', '⚡', '🎵', '🌙', '🔥', '🌊', '🎨', '💫', '🦋', '🗡️', '🌿', '🎮', '🏆']

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function CommunityPage() {
  const { user } = useAuth()
  const navigate  = useNavigate()

  const [groups, setGroups]         = useState([])
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [category, setCategory]     = useState('All')
  const [sort, setSort]             = useState('popular')
  const [showCreate, setShowCreate] = useState(false)

  const debounceRef = useRef(null)

  function load(q = search, cat = category, s = sort) {
    setLoading(true)
    const params = new URLSearchParams({ sort: s })
    if (q)          params.set('search', q)
    if (cat !== 'All') params.set('category', cat)

    getHeaders().then(headers =>
      axios.get(`/api/groups?${params}`, { headers })
        .then(({ data }) => setGroups(data ?? []))
        .catch(() => setGroups([]))
        .finally(() => setLoading(false))
    )
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch(val) {
    setSearch(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => load(val, category, sort), 300)
  }

  function handleCategory(cat) {
    setCategory(cat)
    load(search, cat, sort)
  }

  function handleSort(s) {
    setSort(s)
    load(search, category, s)
  }

  function onGroupCreated(group) {
    setShowCreate(false)
    navigate(`/community/${group.id}`)
  }

  function onJoinToggle(groupId, joined, newCount) {
    setGroups(gs => gs.map(g =>
      g.id === groupId ? { ...g, isMember: joined, memberCount: newCount } : g
    ))
  }

  return (
    <div className="community-page">
      <SEO title="Community" description="Join writing groups, share feedback, and connect with other readers and writers on Verse." />
      <Navbar />

      {/* ── Header ── */}
      <div className="community-header">
        <div className="community-header__blob community-header__blob--1" />
        <div className="community-header__blob community-header__blob--2" />
        <div className="community-header__inner">
          <p className="community-header__eyebrow">✦ {groups.length} groups</p>
          <h1 className="community-header__title">Find your people</h1>
          <p className="community-header__sub">Join groups, share your thoughts, and connect with readers and writers.</p>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <div className="community-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                placeholder="Search groups…"
                value={search}
                onChange={e => handleSearch(e.target.value)}
              />
              {search && <button onClick={() => handleSearch('')}>×</button>}
            </div>

            {user && (
              <button className="btn btn-primary community-create-btn" onClick={() => setShowCreate(true)}>
                + Create group
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="community-filters">
        <div className="community-filters__cats">
          {['All', ...CATEGORIES].map(c => (
            <button
              key={c}
              className={`community-cat-pill${category === c ? ' community-cat-pill--active' : ''}`}
              onClick={() => handleCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="community-filters__sort">
          <button
            className={`community-sort-btn${sort === 'popular' ? ' community-sort-btn--active' : ''}`}
            onClick={() => handleSort('popular')}
          >
            Popular
          </button>
          <button
            className={`community-sort-btn${sort === 'newest' ? ' community-sort-btn--active' : ''}`}
            onClick={() => handleSort('newest')}
          >
            Newest
          </button>
        </div>
      </div>

      {/* ── My Groups ── */}
      {!loading && user && groups.some(g => g.isMember || g.isOwner) && (
        <div className="community-body" style={{ marginBottom: 8 }}>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>My Groups</h2>
          <div className="community-grid">
            {groups.filter(g => g.isMember || g.isOwner).map(g => (
              <GroupCard key={`my-${g.id}`} group={g} currentUser={user} onJoinToggle={onJoinToggle} />
            ))}
          </div>
        </div>
      )}

      {/* ── Grid ── */}
      <div className="community-body">
        {loading ? (
          <div className="community-grid">
            {[...Array(8)].map((_, i) => <div key={i} className="group-card-skeleton" />)}
          </div>
        ) : groups.length === 0 ? (
          <div className="community-empty">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👥</div>
            <p style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 6 }}>No groups found</p>
            <p style={{ color: 'var(--text-faint)', fontSize: '0.9rem', marginBottom: 20 }}>
              {search ? `Nothing matched "${search}"` : 'Be the first to create one.'}
            </p>
            {user && (
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                + Create a group
              </button>
            )}
          </div>
        ) : (
          <div className="community-grid">
            {groups.map(g => (
              <GroupCard key={g.id} group={g} currentUser={user} onJoinToggle={onJoinToggle} />
            ))}
          </div>
        )}
      </div>

      {/* ── Create modal ── */}
      {showCreate && (
        <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={onGroupCreated} />
      )}
    </div>
  )
}

// ─── Group card ────────────────────────────────────────────────────────────────

function GroupCard({ group, currentUser, onJoinToggle }) {
  const [busy, setBusy] = useState(false)

  async function handleJoin(e) {
    e.preventDefault()
    if (!currentUser || busy) return
    setBusy(true)
    try {
      const headers = await getHeaders()
      if (group.isMember) {
        await axios.delete(`/api/groups/${group.id}/join`, { headers })
        onJoinToggle(group.id, false, group.memberCount - 1)
      } else {
        await axios.post(`/api/groups/${group.id}/join`, {}, { headers })
        onJoinToggle(group.id, true, group.memberCount + 1)
      }
    } catch (err) { console.error('[CommunityPage] Failed to toggle group membership:', err) }
    finally { setBusy(false) }
  }

  return (
    <Link to={`/community/${group.id}`} className="group-card">
      <div className="group-card__cover" style={{ background: group.coverUrl ? undefined : group.coverGrad }}>
        {group.coverUrl
          ? <img src={group.coverUrl} alt="" className="group-card__cover-img" loading="lazy" />
          : <span className="group-card__emoji">{group.coverEmoji}</span>
        }
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {group.isPrivate && <span className="group-card__category" style={{ background: 'rgba(0,0,0,0.5)' }}>Private</span>}
          <span className="group-card__category">{group.category}</span>
        </div>
      </div>
      <div className="group-card__body">
        <h3 className="group-card__name">{decodeEntities(group.name)}</h3>
        {group.description && (
          <p className="group-card__desc">{decodeEntities(group.description)}</p>
        )}
        <div className="group-card__meta">
          <span>👥 {formatNum(group.memberCount)} {group.memberCount === 1 ? 'member' : 'members'}</span>
          {group.postCount > 0 && <span>💬 {formatNum(group.postCount)}</span>}
        </div>
        {currentUser && (
          <button
            className={`btn group-card__join-btn${group.isMember ? ' btn-outline' : ' btn-primary'}`}
            onClick={handleJoin}
            disabled={busy}
          >
            {busy ? '…' : group.isMember ? '✓ Joined' : 'Join'}
          </button>
        )}
      </div>
    </Link>
  )
}

// ─── Create group modal ────────────────────────────────────────────────────────

function CreateGroupModal({ onClose, onCreated }) {
  const toast = useToast()
  const [form, setForm] = useState({
    name: '', description: '', category: 'General',
    coverGrad: GRADIENTS[0], coverEmoji: EMOJIS[0], isPrivate: false,
  })
  const [coverFile, setCoverFile] = useState(null)
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setError('') }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Group name is required.'); return }
    setLoading(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post('/api/groups', form, { headers })

      // Optional cover image — additive, fail-soft. The group already exists
      // with its gradient/emoji, so a cover failure must not fail creation.
      if (coverFile) {
        try {
          const blob = await resizeImage(coverFile, { maxEdge: 1920 })
          const upload = blob instanceof File ? blob : new File([blob], coverFile.name, { type: blob.type })
          const formData = new FormData()
          formData.append('file', upload)
          await axios.post(`/api/groups/${data.id}/cover`, formData, { headers })
        } catch {
          toast('Group created, but the cover image didn’t upload. You can add it from group settings.', 'error')
        }
      }

      onCreated(data)
    } catch (err) {
      setError(err.response?.data?.error ?? 'Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="create-group-modal">
        <div className="create-group-modal__header">
          <h2>Create a group</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Preview */}
        <div className="create-group-modal__preview" style={{ background: form.coverGrad }}>
          <span style={{ fontSize: '3rem' }}>{form.coverEmoji}</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
            {form.name || 'Group name'}
          </span>
        </div>

        <form className="create-group-modal__form" onSubmit={handleSubmit}>
          {/* Name */}
          <div className="create-group-modal__field">
            <label>Group name</label>
            <input
              className="auth-input"
              placeholder="My awesome group"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              maxLength={60}
            />
          </div>

          {/* Description */}
          <div className="create-group-modal__field">
            <label>Description <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              className="auth-input create-group-modal__textarea"
              placeholder="What's this group about?"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              maxLength={300}
              rows={3}
            />
          </div>

          {/* Category */}
          <div className="create-group-modal__field">
            <label>Category</label>
            <select className="auth-input" value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Cover gradient */}
          <div className="create-group-modal__field">
            <label>Cover colour</label>
            <div className="create-group-modal__swatches">
              {GRADIENTS.map(g => (
                <button
                  key={g}
                  type="button"
                  className={`swatch${form.coverGrad === g ? ' swatch--active' : ''}`}
                  style={{ background: g }}
                  onClick={() => set('coverGrad', g)}
                />
              ))}
            </div>
          </div>

          {/* Cover image (optional) */}
          <div className="create-group-modal__field">
            <label>Cover photo <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span></label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label className="btn btn-outline" style={{ cursor: 'pointer', fontSize: '0.82rem', padding: '6px 14px' }}>
                {coverFile ? 'Change image' : 'Upload image'}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => { const f = e.target.files?.[0] ?? null; setCoverFile(f); setError('') }} />
              </label>
              {coverFile && (
                <>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{coverFile.name}</span>
                  <button type="button" className="btn" style={{ fontSize: '0.82rem', padding: '6px 14px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setCoverFile(null)}>
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Emoji */}
          <div className="create-group-modal__field">
            <label>Icon</label>
            <div className="create-group-modal__emojis">
              {EMOJIS.map(em => (
                <button
                  key={em}
                  type="button"
                  className={`emoji-pick${form.coverEmoji === em ? ' emoji-pick--active' : ''}`}
                  onClick={() => set('coverEmoji', em)}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>

          {/* Private toggle */}
          <label className="create-group-modal__private">
            <input
              type="checkbox"
              checked={form.isPrivate}
              onChange={e => set('isPrivate', e.target.checked)}
            />
            <span>Private group <span style={{ color: 'var(--text-faint)', fontWeight: 400, fontSize: '0.82rem' }}>(invite only)</span></span>
          </label>

          {error && <div className="auth-error">{error}</div>}

          <div className="create-group-modal__footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
